<script setup lang="ts">
import { generateBrandColorFromLogo } from '~/utils/colorUtils'
import { companyLinks } from '~/utils/menuLinks'
import {
  fetchCompanyData,
  updateCompanyBrandColor
} from '~/utils/companyDataFetcher'
import { generateViewJobPageMeta } from '~/utils/meta/generateViewJobPageMeta'
import type { ApiResponse } from '~~/server/types/response'
import type { CompanyDetails } from '~~/server/types/company'
import RelatedCompanies from '~/components/company/RelatedCompanies.vue'
import SalaryCard from '~/components/company/salary/SalaryCard.vue'
import GeneralDisclaimer from '~/components/common/GeneralDisclaimer.vue'
import SectionHeader from '~/components/common/SectionHeader.vue'
import ActionButton from '~/components/common/ActionButton.vue'
import JobAnalysisDisplay from '~/components/jobs/JobAnalysisDisplay.vue'
import FavoriteButton from '~/components/jobs/FavoriteButton.vue'
import CompanyInfoSidebar from '~/components/company/CompanyInfoSidebar.vue'
import { provideGlobalSalaryPeriod } from '~/composables/useGlobalSalaryPeriod'
import { generateSalaryDescription } from '~/utils/salaryDescription'
import { formatLocation, proceedWithJobApplication, throwJobNotFoundError } from '~/utils/jobHelpers'
import { cleanJobDescription } from '~/utils/jobDescription'
import { proxyImageUrl } from '~/utils/imageProxy'

definePageMeta({
  layout: 'marketing',
  middleware: ['company-validation']
})
const route = useRoute()
const companySlug = computed(() => route.params.permalink as string)
const jobId = computed(() => route.params.id as string)
const { session } = useUserSession()
provide('session', session)
const salaryPeriodState = provideGlobalSalaryPeriod()
const { adjustValueForPeriod } = salaryPeriodState
const { country } = useCountry()
const { device } = useDevice()
const send_id = computed(() => (route.query.send_id as string) || '')
const { data: pageData, pending } = await useAsyncData(
  `company-job-${companySlug.value}-${jobId.value}`,
  async () => {
    try {
      const company = (await fetchCompanyData(
        companySlug.value
      )) as ApiResponse<CompanyDetails>

      // Try to fetch from ls-jobs first
      let jobData = null
      try {
        jobData = await $fetch<any>(
          `/api/jobs/${country.value}/search`,
          {
            method: 'POST',
            body: {
              job_id: jobId.value,
              keyword: 'search',
              location: '10001',
              device: device.value,
              send_id: send_id.value
            }
          }
        )
      } catch (err) {
        // ls-jobs fetch failed, will try organic fallback
      }

      // Always fetch organic job data for salary info and as fallback
      let organicJobData = null
      try {
        organicJobData = await $fetch<any>(
          `/api/jobs/organic/${jobId.value}`
        )
      } catch (err) {
      }

      // If ls-jobs returned a job, enrich it with organic data
      if (jobData?.success && jobData?.job) {
        if (organicJobData?.success && organicJobData?.data) {
          if (organicJobData.data.estimated_salary) {
            jobData.job.estimated_salary = organicJobData.data.estimated_salary
          }
          if (organicJobData.data.soc_code) {
            jobData.job.soc_code = organicJobData.data.soc_code
          }
          if (organicJobData.data.category) {
            jobData.job.category = organicJobData.data.category
          }
          // Add organic job URL as fallback for apply button
          if (organicJobData.data.url) {
            jobData.job.organic_url = organicJobData.data.url
          }
          // Add geo coordinates for schema if available
          if (organicJobData.data.latlon) {
            jobData.job.latlon = organicJobData.data.latlon
          }
          // Add zip to location if missing
          if (organicJobData.data.zip && jobData.job.location && !jobData.job.location.zip) {
            jobData.job.location.zip = organicJobData.data.zip
          }
          // Add experience requirements and job benefits for schema
          if (organicJobData.data.experience_requirements) {
            jobData.job.experience_requirements = organicJobData.data.experience_requirements
          }
          if (organicJobData.data.job_benefits) {
            jobData.job.job_benefits = organicJobData.data.job_benefits
          }
        }
      }
      // If ls-jobs failed but organic job exists, use organic data as fallback
      else if (organicJobData?.success && organicJobData?.data) {
        const organic = organicJobData.data
        jobData = {
          success: true,
          job: {
            j_id: organic.job_id,
            title: organic.title,
            company: organic.company,
            description: organic.description,
            location: organic.location || {
              city: organic.city,
              state: organic.state,
              zip: organic.zip,
              country: organic.country || 'United States'
            },
            date: organic.date,
            logo: organic.company_logo_url || company?.data?.logo_url,
            url: organic.url, // Use organic job URL for apply button
            estimated_salary: organic.estimated_salary,
            soc_code: organic.soc_code,
            category: organic.category,
            latlon: organic.latlon, // Geo coordinates for schema
            experience_requirements: organic.experience_requirements,
            job_benefits: organic.job_benefits,
            is_organic_fallback: true // Flag to indicate this is from organic_jobs
          }
        }
      }

      let aiAnalysis = null
      let indexationData = null
      try {
        const aiData = await $fetch<any>(
          `/api/jobs/indexation/${jobId.value}`
        )
        if (aiData?.success && aiData?.data) {
          aiAnalysis = aiData.data.ai_analysis
          indexationData = {
            created_at: aiData.data.created_at,
            date: aiData.data.date
          }
        }
      } catch (err) {
      }
      return {
        company,
        jobData,
        aiAnalysis,
        indexationData
      }
    } catch (err) {
      throw err
    }
  },
  { server: true }
)
const isJobNotFound = computed(() => {
  return !pageData.value || !pageData.value.jobData?.success || !pageData.value.jobData?.job
})

// Set 404 status code for expired jobs (SSR)
if (isJobNotFound.value) {
  const event = useRequestEvent()
  if (event) {
    setResponseStatus(event, 404)
  }
  console.log(`[Expired Job] https://employers.io/company/${companySlug.value}/viewJob/${jobId.value}`)
}
const company = computed(() => {
  return pageData.value?.company?.success && pageData.value.company.data
    ? pageData.value.company.data
    : null
})
const job = computed(() => {
  if (!pageData.value?.jobData?.success || !pageData.value?.jobData?.job) {
    return null
  }
  const jobData = pageData.value.jobData
  if (company.value?.logo_url && jobData?.job) {
    return {
      ...jobData,
      job: {
        ...jobData.job,
        logo: company.value.logo_url
      }
    }
  }
  return jobData
})
provide('company', company)
const aiAnalysis = computed(() => {
  return pageData.value?.aiAnalysis || null
})
const indexationData = computed(() => {
  return pageData.value?.indexationData || null
})
// Use created_at from indexation table if available, otherwise fall back to job.date
const displayDate = computed(() => {
  return indexationData.value?.created_at || indexationData.value?.date || job.value?.job?.date
})
const brandColor = ref('#5F0CE2')
watch(
  () => company.value,
  async (newCompany) => {
    if (!newCompany) {
      brandColor.value = '#5F0CE2'
      return
    }
    if (newCompany.brand_color) {
      brandColor.value = newCompany.brand_color
      return
    }
    if (newCompany.logo_url) {
      try {
        const generatedColor = await generateBrandColorFromLogo(
          newCompany.logo_url
        )
        brandColor.value = generatedColor || '#5F0CE2'
        if (generatedColor) {
          const response = await updateCompanyBrandColor(
            companySlug.value,
            generatedColor
          )
          if (!response.success) {
          }
        }
      } catch (error) {
        brandColor.value = '#5F0CE2'
      }
    }
  },
  { immediate: true }
)
const tabs = computed(() => {
  return companyLinks(companySlug.value).map(link => ({
    name: link.name,
    to: { path: link.to.replace('%s', companySlug.value) },
    value: link.value
  }))
})
const meta = ref({ description: '' })
watchOnce(
  () => job.value?.job,
  (jobData) => {
    if (jobData) {
      const brandName = company.value?.brand_name || company.value?.company_name || jobData.company
      const isNotInJobIndexation = !aiAnalysis.value
      const schemaDate = indexationData.value?.created_at || indexationData.value?.date || jobData.date
      // Attach ai_analysis to the job object for schema generation (experienceRequirements, jobBenefits, etc.)
      const jobDataForSchema = {
        ...jobData,
        date: schemaDate,
        ai_analysis: aiAnalysis.value || jobData.ai_analysis
      }
      meta.value = generateViewJobPageMeta(route, [jobDataForSchema], brandName, { noIndex: isNotInJobIndexation })
    }
  },
  { immediate: true }
)
const salaryDescription = computed(() => {
  if (!job.value?.job?.estimated_salary) return ''
  const salaryData = {
    count: 1,
    adjusted_salary: job.value.job.estimated_salary.adjusted_salary
  }
  const location = {
    city: job.value.job.location.city,
    state: job.value.job.location.state
  }
  const roleTitle = job.value.job.estimated_salary.job_title || job.value.job.title
  return generateSalaryDescription(
    salaryData,
    company.value?.brand_name || company.value?.company_name || '',
    location,
    roleTitle,
    adjustValueForPeriod
  )
})
const showReportModal = ref(false)
const handleReportJob = () => {
  showReportModal.value = true
}
const formatJobDate = (dateString: string) => {
  try {
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = Math.abs(now.getTime() - date.getTime())
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
    if (diffDays === 0) return 'Posted today'
    if (diffDays === 1) return 'Posted yesterday'
    if (diffDays < 7) return `Posted ${diffDays} days ago`
    if (diffDays < 30) return `Posted ${Math.floor(diffDays / 7)} weeks ago`
    if (diffDays < 365) return `Posted ${Math.floor(diffDays / 30)} months ago`
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  } catch (e) {
    return dateString
  }
}
const { loggedIn } = useUserSession()
const applyForJob = () => {
  if (!job.value?.job) return
  // Create a job object with organic_url as fallback for url
  const jobForApplication = {
    ...job.value.job,
    url: job.value.job.url || job.value.job.organic_url
  }
  proceedWithJobApplication(jobForApplication, '', 0)
}
const applyButtonRef = ref<HTMLElement | null>(null)
const applyButtonInnerRef = ref<HTMLElement | null>(null)
const isButtonFixed = ref(false)
const buttonPlaceholderHeight = ref(0)
onMounted(() => {
  const handleScroll = () => {
    if (!applyButtonRef.value || !applyButtonInnerRef.value) return
    const viewportHeight = window.innerHeight
    const containerRect = applyButtonRef.value.getBoundingClientRect()
    const placeholderTop = containerRect.top
    const placeholderBottom = containerRect.top + buttonPlaceholderHeight.value
    if (!isButtonFixed.value) {
      const buttonRect = applyButtonInnerRef.value.getBoundingClientRect()
      if (buttonRect.bottom >= viewportHeight) {
        buttonPlaceholderHeight.value = applyButtonInnerRef.value.offsetHeight
        isButtonFixed.value = true
      }
    } else {
      if (placeholderBottom <= viewportHeight) {
        isButtonFixed.value = false
      }
    }
  }
  window.addEventListener('scroll', handleScroll, { passive: true })
  handleScroll()
  onBeforeUnmount(() => {
    window.removeEventListener('scroll', handleScroll)
  })
})
</script>

<template>
  <div>
    <div
      v-if="pending"
      class="text-center py-12"
    >
      <div class="animate-pulse">
        <div class="h-24 w-24 bg-gray-200 rounded-full mx-auto mb-6" />
        <div class="h-8 w-64 bg-gray-200 rounded mx-auto" />
      </div>
    </div>
    <template v-else-if="company && job">
      <CompanyHeader
        :company="company"
        :brand-color="brandColor"
        :has-review="false"
        :section="'jobs'"
      />
      <CompanyHeroBanner
        :brand-color="brandColor"
        :brand-name="company.brand_name"
        :hero-url="company.hero_url"
        :section="'jobs'"
        :job-title="job.job.title"
        :job-location="job.job.location"
      />
      <CompanyNavigation
        :company-slug="companySlug"
        :current-section="'jobs'"
        :tabs="tabs"
      />
      <main class="pb-8">
        <Container>
          <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
            <div class="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div class="lg:col-span-8">
                <div class="lg:p-0 p-4 bg-white rounded-lg mb-6">
                  <div class="flex justify-between items-start gap-4 mb-4">
                    <h1 class="text-2xl font-bold text-gray-900">
                      {{ job.job.title }}
                    </h1>
                    <div class="hidden lg:flex items-center gap-4">
                      <FavoriteButton :job="job.job" />
                      <Button
                        size="lg"
                        color="black"
                        variant="solid"
                        class="text-white"
                        @click="applyForJob"
                      >
                        Apply
                      </Button>
                    </div>
                    <div class="flex items-center lg:hidden">
                      <FavoriteButton :job="job.job" />
                    </div>
                  </div>
                  <p class="text-gray-500 font-medium mt-1">
                    {{ company?.brand_name || company?.company_name || job.job.company }}
                  </p>
                  <div class="flex items-center flex-wrap gap-x-4 gap-y-2 text-gray-600 mt-3">
                    <div class="flex items-center gap-2 whitespace-nowrap">
                      <Icon
                        name="i-heroicons-map-pin"
                        class="w-5 h-5 flex-shrink-0"
                      />
                      <span>{{ formatLocation(job.job.location) }}</span>
                    </div>
                    <div
                      v-if="displayDate"
                      class="flex items-center gap-2 whitespace-nowrap"
                    >
                      <Icon
                        name="i-heroicons-clock"
                        class="w-5 h-5 flex-shrink-0"
                      />
                      <span>{{ formatJobDate(displayDate) }}</span>
                    </div>
                    <div
                      v-if="aiAnalysis?.core_information?.work_schedule"
                      class="flex items-center gap-2 whitespace-nowrap"
                    >
                      <Icon
                        name="i-heroicons-calendar-days"
                        class="w-5 h-5 flex-shrink-0"
                      />
                      <span>{{ aiAnalysis.core_information.work_schedule }}</span>
                    </div>
                    <div
                      v-if="aiAnalysis?.core_information?.remote_status"
                      class="flex items-center gap-2 whitespace-nowrap"
                    >
                      <Icon
                        name="i-heroicons-home"
                        class="w-5 h-5 flex-shrink-0"
                      />
                      <span>{{ aiAnalysis.core_information.remote_status }}</span>
                    </div>
                    <div
                      v-if="aiAnalysis?.core_information?.start_date"
                      class="flex items-center gap-2 whitespace-nowrap"
                    >
                      <Icon
                        name="i-heroicons-calendar"
                        class="w-5 h-5 flex-shrink-0"
                      />
                      <span>Start: {{ aiAnalysis.core_information.start_date }}</span>
                    </div>
                    <div
                      v-if="aiAnalysis?.core_information?.travel_requirements"
                      class="flex items-center gap-2 whitespace-nowrap"
                    >
                      <Icon
                        name="i-heroicons-globe-alt"
                        class="w-5 h-5 flex-shrink-0"
                      />
                      <span>{{ aiAnalysis.core_information.travel_requirements }}</span>
                    </div>
                  </div>
                </div>
                <div
                  v-if="aiAnalysis"
                  class="mb-6"
                >
                  <JobAnalysisDisplay
                    :analysis="aiAnalysis"
                    @apply="applyForJob"
                  />
                </div>
                <div
                  v-else
                  class="bg-gray-50 rounded-lg p-6"
                >
                  <div
                    class="job-description text-gray-600 leading-relaxed"
                    v-html="cleanJobDescription(job.job.description)"
                  />
                </div>
                <div
                  ref="applyButtonRef"
                  class="lg:hidden"
                >
                  <div
                    v-if="isButtonFixed"
                    :style="{ height: `${buttonPlaceholderHeight}px` }"
                  />
                  <div
                    ref="applyButtonInnerRef"
                    :class="[
                      isButtonFixed ? 'fixed bottom-0 left-0 right-0 shadow-lg bg-white border-t border-gray-200 rounded-none p-4 z-50'
                      : 'mt-4'
                    ]"
                  >
                    <div :class="isButtonFixed ? 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8' : ''">
                      <Button
                        size="lg"
                        color="black"
                        variant="solid"
                        class="text-white w-full justify-center"
                        @click="applyForJob"
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </div>
                <div class="mt-4 flex justify-end">
                  <Button
                    variant="ghost"
                    color="gray"
                    size="sm"
                    @click="handleReportJob"
                  >
                    <template #leading>
                      <Icon
                        name="i-heroicons-flag"
                        class="w-4 h-4"
                      />
                    </template>
                    Report Job
                  </Button>
                </div>
              </div>
              <div class="hidden lg:block lg:col-span-4">
                <div class="lg:sticky lg:top-[calc(222px-2rem)]">
                  <CompanyInfoSidebar
                    :company="company"
                    :brand-color="brandColor"
                    max-height="calc(100vh - 222px)"
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="job.job.estimated_salary"
            class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8"
          >
            <SectionHeader
              title="Estimated Salary"
              class="mb-6"
            >
              <template #action>
                <NuxtLink
                  :to="`/company/${companySlug}/salaries`"
                  class="hidden sm:block"
                >
                  <ActionButton
                    text="See all salaries"
                    icon="i-heroicons-arrow-right"
                    size="sm"
                    variant="ghost"
                  />
                </NuxtLink>
              </template>
            </SectionHeader>
            <div class="block sm:hidden mb-4">
              <NuxtLink :to="`/company/${companySlug}/salaries`">
                <ActionButton
                  text="See all salaries"
                  icon="i-heroicons-arrow-right"
                  size="sm"
                  variant="ghost"
                  class="justify-start"
                />
              </NuxtLink>
            </div>
            <SalaryCard
              :company-name="company.brand_name || company.company_name"
              :permalink="companySlug"
              :pre-fetched-data="job.job.estimated_salary"
              :force-server-render="true"
              :description-text="salaryDescription"
            />
          </div>
          <RelatedCompanies
            v-if="company.related_companies"
            :companies="company.related_companies"
            :company-name="company.brand_name || company.company_name"
            :section="'jobs'"
          />
          <ShareOnSocialMedia
            class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
            :text="`Check out this job ! ${meta.description || ''}`"
            :title="`Share This Job`"
          />
          <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
            <div class="flex flex-wrap gap-4">
              <Button
                to="/company"
                variant="outline"
              >
                Back to Companies
              </Button>
              <Button
                :to="`/company/${companySlug}/jobs`"
                variant="outline"
              >
                All Jobs
              </Button>
            </div>
          </div>
          <GeneralDisclaimer
            v-if="job.job.estimated_salary"
            disclaimer="Figures represent approximate ranges and may vary based on experience, location, and other factors. For the most accurate information, please consult the employer directly."
            link-text="Contact us"
            link-to="/employers/contact"
            post-text="to suggest updates to this information."
            class="mt-8"
          />
        </Container>
      </main>
      <ReportJobModal
        v-model="showReportModal"
        :job-id="jobId"
        :job-title="job.job.title"
        :company="job.job.company"
        :job-url="`https://employers.io/company/${companySlug}/viewJob/${jobId}`"
      />
    </template>
    <!-- Job Not Found / Expired -->
    <ErrorsErrorPage
      v-else
      :status-code="404"
      title="This job posting has expired."
      subtitle="Don't worry, there are plenty more opportunities waiting for you!"
      :show-company-jobs-button="true"
      :company-slug="companySlug"
    />
  </div>
</template>

